import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useGithubRepos, useImportBook } from '@/hooks/useGithubRepos'

interface Props {
  githubConnected: boolean
  onClose: () => void
}

export default function ImportFromGithub({ githubConnected, onClose }: Props) {
  const { data: repos, isLoading, error: loadError } = useGithubRepos(githubConnected)
  const importBook = useImportBook()
  const [filter, setFilter] = useState('')
  const [selected, setSelected] = useState<string | null>(null)

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase()
    if (!q) return repos ?? []
    return (repos ?? []).filter(
      (r) => r.name.toLowerCase().includes(q) || (r.description ?? '').toLowerCase().includes(q),
    )
  }, [repos, filter])

  const onImport = async () => {
    if (!selected) return
    try {
      await importBook.mutateAsync(selected)
      onClose()
    } catch {
      // error surfaced from importBook.error below
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4" onClick={onClose}>
      <div
        className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Import from GitHub</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700">✕</button>
        </div>

        {!githubConnected ? (
          <div className="mt-4 text-sm text-slate-600">
            <p>Connect your GitHub account first to import a book repo.</p>
            <Link
              to="/settings"
              className="mt-3 inline-block rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800"
            >
              Go to Settings
            </Link>
          </div>
        ) : (
          <>
            <p className="mt-1 text-xs text-slate-500">
              Pick a repo with a TransliFlow <code>book.yml</code>. We'll pull its content and keep it
              synced.
            </p>

            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter repositories…"
              className="mt-3 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
            />

            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-200">
              {isLoading && <p className="p-3 text-sm text-slate-400">Loading repositories…</p>}
              {loadError && (
                <p className="p-3 text-sm text-red-600">Couldn't load repositories.</p>
              )}
              {!isLoading && !loadError && filtered.length === 0 && (
                <p className="p-3 text-sm text-slate-400">No repositories found.</p>
              )}
              <ul>
                {filtered.map((r) => (
                  <li key={r.fullName}>
                    <button
                      onClick={() => setSelected(r.name)}
                      className={`flex w-full flex-col items-start px-3 py-2 text-left hover:bg-slate-50 ${
                        selected === r.name ? 'bg-slate-100' : ''
                      }`}
                    >
                      <span className="text-sm font-medium text-slate-900">{r.name}</span>
                      {r.description && (
                        <span className="line-clamp-1 text-xs text-slate-500">{r.description}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {importBook.error && (
              <p className="mt-2 text-sm text-red-600">
                {importBook.error instanceof Error ? importBook.error.message : 'Import failed'}
              </p>
            )}

            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={onClose}
                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
              >
                Cancel
              </button>
              <button
                onClick={onImport}
                disabled={!selected || importBook.isPending}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50"
              >
                {importBook.isPending ? 'Importing…' : 'Import'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
